import { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import React, { useEffect, useState } from 'react';
import ArticleGrid from '../../../../../components/article-grid/article-grid';
import { BreadcrumbItem } from '../../../../../components/breadcrumb/breadcrumb';
import CategoryHero from '../../../../../components/category-hero/category-hero';
import DefaultLayout from '../../../../../components/layouts/default-layout';
import NewsletterSignUpSection from '../../../../../components/newsletter-signup-section/newsletter-signup-section';
import { getAllCategories, getArticlesByCategory, getCategoryFromName } from '../../../../../helpers/BuilderHelper';
import { ALL_LOCALES, localifyLink, useLocale } from '../../../../../helpers/LangHelper';
import { CategoryCtx } from '../../../../../shared/sharedContext';
import { Article } from '../../../../../types/Article';
import { Category } from '../../../../../types/Category';

import * as style from '../../../../../styles/generic.module.scss';
import MetaBlock from '../../../../../components/meta-block/MetaBlock';
import { getCommonPageDetails } from '../../../../../shared/commonPageDetails';

export async function getStaticProps({ params }: GetStaticPropsContext<any>) {
    const category = await getCategoryFromName(params.category);
    let pageSlug = `/${params.business}/access/${params.category}`;

    let initialArticles: Article[] = [];
    if (category) {
        initialArticles = await getArticlesByCategory({ locale: params.lang, limit: 9, categoryIds: [category?.id] });
    }

    const { footerItems, categories, canonicalUrl } = await getCommonPageDetails(pageSlug, params.lang);

    return {
        props: { category, initialArticles, footerItems, categories, canonicalUrl },
        revalidate: 1600,
    };
}

export async function getStaticPaths() {
    const pages = await getAllCategories();

    const allPaths: string[] = [];
    pages.forEach((page) => {
        if (page.categoryUrl) {
            ALL_LOCALES.forEach((langs) => {
                allPaths.push(localifyLink(`/${langs}${page.categoryUrl}`, langs));
            });
        }
    });

    return {
        paths: allPaths,
        fallback: false,
    };
}

const CategoryPage = ({ category, initialArticles, footerItems, categories }: InferGetStaticPropsType<typeof getStaticProps>) => {
    const { locale } = useLocale();

    const [articles, setArticles] = useState<Article[]>(initialArticles ?? []);
    const [categoriesToFilter, setCategoriesToFilter] = useState<Category[]>([]);
    const [allCategories, setAllCategories] = useState<Category[]>([]);
    const [offsetArticle, setOffsetArticle] = useState<number>(0);
    const [isArticleLoadMore, setIsArticleLoadMore] = useState<boolean>(false);

    useEffect(() => {
        if (offsetArticle !== 0) {
            loadMoreArticles();
        }
    }, [offsetArticle]);

    if (!category) {
        return null;
    }

    const loadMoreArticles = async () => {
        await setIsArticleLoadMore(true);
        const articlesByCategory = await getArticlesByCategory({ locale, offset: offsetArticle, limit: 10, categoryIds: [category?.id] });
        await setArticles([...articles.concat(articlesByCategory)]);
        await setIsArticleLoadMore(false);
    };

    const categoryBreadcrumbs: BreadcrumbItem[] = [
        { name: 'Business', link: '/business' },
        { name: 'Access', link: '/business/access' },
        { name: category.name, link: '' },
    ];

    const categoryTitleName = category.longName ?? category.name;

    return (
        <DefaultLayout footerItems={footerItems} categories={categories}>
            <MetaBlock title={categoryTitleName} description={category.description} leadImage={category.image} />
            <CategoryCtx.Provider value={{ allCategories, categoriesToFilter, setCategoriesToFilter, setAllCategories }}>
                <CategoryHero categoryHeroTitle={categoryTitleName} breadcrumbs={categoryBreadcrumbs} backgroundColor={category.isSeries ? 'Blum' : 'Orca Pastel'} />
                <div className={(style as any)['page-layout']}>
                    <ArticleGrid {...{ articles, setOffsetArticle, offsetArticle, isLoadingMoreArticles: isArticleLoadMore }} />
                </div>
            </CategoryCtx.Provider>
            <NewsletterSignUpSection />
        </DefaultLayout>
    );
};
export default CategoryPage;
